import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../shared/context/UserContext';
import {
  getProjectDetail, getProjectTools, getBolList,
  updateScrapProject, closeScrapProject, addToolsToProject,
  writeOffTool, removeToolFromProject, reopenBol, createManualBol,
} from '../../api/disposalApi';
import ScrapProjectStatusBadge from '../../components/disposal/ScrapProjectStatusBadge';
import DisposalStatusBadge from '../../components/disposal/DisposalStatusBadge';
import BolStatusBadge from '../../components/disposal/BolStatusBadge';
import ToolPickerModal from '../../components/disposal/ToolPickerModal';
import './ScrapProjectDetailPage.css';

function fmtDate(v) { if (!v) return '—'; try { return new Date(v).toLocaleDateString('en-US'); } catch { return v; } }
function fmtDatetime(v) { if (!v) return '—'; try { return new Date(v).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }); } catch { return v; } }

const DISPOSAL_ORDER = { ASSIGNED: 0, STAGED: 1, ON_SHIPMENT: 2, SHIPPED: 3, WRITTEN_OFF: 4 };

function Chevron({ open }) {
  return (
    <span className={`spd-card-chevron ${open ? 'open' : ''}`}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </span>
  );
}

/* ── NBR sequence helper ──────────────────────────────────────────────────── */

function getNbrSequence(bols, projectNumber) {
  let max = 0;
  const re = new RegExp(`^NBR-${projectNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-(\\d+)$`);
  for (const b of bols) {
    const m = (b.bol_number || '').match(re);
    if (m) { const n = parseInt(m[1], 10); if (n > max) max = n; }
  }
  return String(max + 1).padStart(3, '0');
}

/* ── ManualBolModal ──────────────────────────────────────────────────────── */

function ManualBolModal({ projectId, siteId, projectNumber, existingBols, tools: projectTools, onClose, onCreated, callerEmail }) {
  const [bolNumber, setBolNumber] = useState(() => `NBR-${projectNumber}-${getNbrSequence(existingBols, projectNumber)}`);
  const [truckType, setTruckType] = useState('');
  const [haulerCompany, setHaulerCompany] = useState('');
  const [truckNumber, setTruckNumber] = useState('');
  const [driverName, setDriverName] = useState('');
  const [closedAt, setClosedAt] = useState(() => new Date().toLocaleDateString('en-CA'));
  const [selectedTools, setSelectedTools] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const assignableTools = useMemo(() =>
    (projectTools || []).filter(t => {
      const ds = (t.disposal_status || '').toUpperCase();
      return ds === 'ASSIGNED' || ds === 'STAGED' || ds === 'ELIGIBLE';
    }),
    [projectTools]
  );

  function toggleTool(toolId) {
    setSelectedTools(prev => {
      const next = new Set(prev);
      if (next.has(toolId)) next.delete(toolId); else next.add(toolId);
      return next;
    });
  }

  const isValid = bolNumber.trim() && truckType && haulerCompany.trim();

  async function handleSubmit() {
    if (!isValid) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        bol_id: crypto.randomUUID(),
        bol_number: bolNumber.trim(),
        project_id: projectId,
        site_id: siteId,
        truck_type: truckType,
        hauler_company: haulerCompany.trim(),
        source: 'MANUAL',
        created_by: callerEmail,
        tools: Array.from(selectedTools),
        closed_at: closedAt,
      };
      if (truckNumber.trim()) payload.truck_number = truckNumber.trim();
      if (driverName.trim()) payload.driver_name = driverName.trim();
      await createManualBol(payload, callerEmail);
      onCreated();
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  const fieldStyle = {
    width: '100%', padding: 'var(--space-4) var(--space-5)',
    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
    background: 'var(--color-bg-input)', color: 'var(--color-text-primary)',
    fontSize: 'var(--text-sm)', fontFamily: 'var(--font-family)', boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)',
    color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)',
    textTransform: 'uppercase', letterSpacing: '0.03em',
  };
  const fieldGroup = { marginBottom: 'var(--space-5)' };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'var(--color-overlay)', zIndex: 'var(--z-modal)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: 'var(--color-bg-card)', borderRadius: 'var(--radius-xl)',
        maxWidth: '600px', width: '90vw', maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: 'var(--shadow-xl)',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--space-7) var(--space-8)',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-text-heading)' }}>
              Create BOL
            </h2>
            <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              Manually record a bill of lading
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 'var(--text-xl)',
            color: 'var(--color-text-muted)', cursor: 'pointer', padding: 'var(--space-2)',
            fontFamily: 'var(--font-family)',
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-6) var(--space-8)' }}>
          <div style={fieldGroup}>
            <label style={labelStyle}>BOL Number *</label>
            <input type="text" style={fieldStyle} value={bolNumber}
              onChange={e => setBolNumber(e.target.value)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)' }}>
            <div style={fieldGroup}>
              <label style={labelStyle}>Truck Type *</label>
              <select style={fieldStyle} value={truckType} onChange={e => setTruckType(e.target.value)}>
                <option value="">Select…</option>
                <option value="FLATBED">FLATBED</option>
                <option value="ROLL-OFF">ROLL-OFF</option>
                <option value="OTHER">OTHER</option>
              </select>
            </div>
            <div style={fieldGroup}>
              <label style={labelStyle}>Hauler Company *</label>
              <input type="text" style={fieldStyle} value={haulerCompany}
                onChange={e => setHaulerCompany(e.target.value)} placeholder="Company name" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)' }}>
            <div style={fieldGroup}>
              <label style={labelStyle}>Truck Number</label>
              <input type="text" style={fieldStyle} value={truckNumber}
                onChange={e => setTruckNumber(e.target.value)} />
            </div>
            <div style={fieldGroup}>
              <label style={labelStyle}>Driver Name</label>
              <input type="text" style={fieldStyle} value={driverName}
                onChange={e => setDriverName(e.target.value)} />
            </div>
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Closed At</label>
            <input type="date" style={fieldStyle} value={closedAt}
              onChange={e => setClosedAt(e.target.value)} />
          </div>

          {/* Tool selection */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Tools (optional)</label>
            {assignableTools.length === 0 ? (
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', padding: 'var(--space-4) 0' }}>
                No assignable tools available.
              </div>
            ) : (
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                {assignableTools.map(t => {
                  const tid = t.id || t.tool_id;
                  return (
                    <label key={tid} style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                      padding: 'var(--space-3) var(--space-4)',
                      borderBottom: '1px solid var(--color-border)', cursor: 'pointer',
                      fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)',
                    }}>
                      <input type="checkbox" checked={selectedTools.has(tid)}
                        onChange={() => toggleTool(tid)} />
                      <span style={{ fontWeight: 'var(--font-semibold)', fontFamily: 'var(--font-mono)' }}>
                        {t.supplier_tool_id || tid}
                      </span>
                      <span style={{ color: 'var(--color-text-muted)' }}>
                        {t.estimated_weight ? `${Number(t.estimated_weight).toLocaleString()} lbs` : ''}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {submitError && (
            <div style={{ color: 'var(--color-danger)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-4)' }}>
              {submitError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-4)',
          padding: 'var(--space-6) var(--space-8)',
          borderTop: '1px solid var(--color-border)',
        }}>
          <button className="spd-btn spd-btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="spd-btn spd-btn-primary" onClick={handleSubmit} disabled={!isValid || submitting}>
            {submitting ? 'Creating…' : 'Create BOL'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */

export default function ScrapProjectDetailPage() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { callerEmail, userProfile } = useUser();
  const siteId = location.state?.site_id || '';

  const [project, setProject] = useState(null);
  const [tools, setTools] = useState([]);
  const [bols, setBols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Section collapse (persisted)
  const collapseKey = (section) => `spd_collapse_${projectId}_${section}`;
  function initCollapse(section) {
    try { return localStorage.getItem(collapseKey(section)) === 'true'; } catch { return false; }
  }
  const [detailsCollapsed, setDetailsCollapsed] = useState(() => initCollapse('details'));
  const [toolsCollapsed, setToolsCollapsed] = useState(() => initCollapse('tools'));
  const [bolsCollapsed, setBolsCollapsed] = useState(() => initCollapse('bols'));

  function toggleSection(section, val, setter) {
    setter(val);
    try { localStorage.setItem(collapseKey(section), String(val)); } catch {}
  }

  // Edit mode for details
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Add tools modal
  const [showToolPicker, setShowToolPicker] = useState(false);

  // Manual BOL modal
  const [showManualBolModal, setShowManualBolModal] = useState(false);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3000); }

  // Load all data
  const loadAll = useCallback(async () => {
    if (!projectId || !callerEmail) return;
    setLoading(true);
    setError(null);
    try {
      const [pRes, tRes, bRes] = await Promise.all([
        getProjectDetail(projectId, siteId, callerEmail),
        getProjectTools(projectId, callerEmail),
        getBolList(projectId, callerEmail),
      ]);
      setProject(pRes.result || null);
      setTools(tRes.results || []);
      setBols(bRes.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, siteId, callerEmail]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Edit helpers
  function startEdit() {
    if (!project) return;
    setEditForm({
      project_number: project.project_number || '',
      oem: project.oem || '',
      supplier_name: project.supplier_name || '',
      site_name: project.site_name || '',
      site_city: project.site_city || '',
      site_state: project.site_state || '',
      hauler_name: project.hauler_name || '',
      estimated_weight_lbs: project.estimated_weight_lbs ?? '',
      price_per_ton: project.price_per_ton ?? '',
      notes: project.notes || '',
    });
    setSaveError(null);
    setEditing(true);
  }

  async function handleSave() {
    setSaving(true); setSaveError(null);
    try {
      await updateScrapProject({
        project_id: projectId, site_id: siteId, ...editForm,
        estimated_weight_lbs: editForm.estimated_weight_lbs !== '' ? parseFloat(editForm.estimated_weight_lbs) : null,
        price_per_ton: editForm.price_per_ton !== '' ? parseFloat(editForm.price_per_ton) : null,
      }, callerEmail);
      setEditing(false);
      showToast('Project saved');
      loadAll();
    } catch (err) { setSaveError(err.message); }
    finally { setSaving(false); }
  }

  async function handleClose() {
    if (!window.confirm('Close this project? All tools not yet shipped will be marked Written Off. This cannot be undone.')) return;
    try {
      const r = await closeScrapProject(projectId, siteId, callerEmail);
      showToast(`Project closed. ${r.written_off_count || 0} tools written off.`);
      loadAll();
    } catch (err) { showToast(err.message); }
  }

  async function handleAddTools(toolIds) {
    setShowToolPicker(false);
    try {
      await addToolsToProject(projectId, siteId, toolIds, callerEmail);
      showToast(`${toolIds.length} tools added to project.`);
      loadAll();
    } catch (err) { showToast(err.message); }
  }

  async function handleWriteOff(toolId) {
    if (!window.confirm('Mark this tool as written off?')) return;
    try {
      await writeOffTool(projectId, siteId, toolId, callerEmail);
      showToast('Tool marked as written off.');
      loadAll();
    } catch (err) { showToast(err.message); }
  }

  // Remove from project — confirm dialog state
  const [removeTarget, setRemoveTarget] = useState(null); // tool_id or null
  const [removeReason, setRemoveReason] = useState('');
  const [removeError, setRemoveError] = useState(null);
  const [removing, setRemoving] = useState(false);

  const canRemoveTools = userProfile?.role && ['ADMIN', 'PROJECT_LEAD', 'DISPOSAL_LEAD'].includes(userProfile.role);

  async function handleRemoveConfirm() {
    if (!removeTarget) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await removeToolFromProject(projectId, removeTarget, callerEmail, removeReason);
      setRemoveTarget(null);
      setRemoveReason('');
      showToast('Tool removed from project.');
      loadAll();
    } catch (err) {
      setRemoveError(err.message);
    } finally {
      setRemoving(false);
    }
  }

  async function handleReopenBol(bolId) {
    if (!window.confirm('Reopen this BOL? Tools on this shipment will return to Assigned status.')) return;
    try {
      const r = await reopenBol(bolId, projectId, callerEmail);
      showToast(`BOL reopened. ${r.tools_returned || 0} tools returned to assigned.`);
      loadAll();
    } catch (err) { showToast(err.message); }
  }

  const isActive = project && ['READY', 'IN_PROGRESS'].includes((project.status || '').toUpperCase());

  // Tool counts
  const toolCounts = useMemo(() => {
    const c = { total: tools.length, ASSIGNED: 0, STAGED: 0, ON_SHIPMENT: 0, SHIPPED: 0, WRITTEN_OFF: 0 };
    tools.forEach(t => { const s = (t.disposal_status || '').toUpperCase(); if (c[s] !== undefined) c[s]++; });
    return c;
  }, [tools]);

  const bolLookup = useMemo(() => {
    const m = {};
    for (const b of bols) { if (b.bol_id || b.id) m[b.bol_id || b.id] = b.bol_number || null; }
    return m;
  }, [bols]);

  const sortedTools = useMemo(() =>
    [...tools].sort((a, b) => (DISPOSAL_ORDER[(a.disposal_status || '').toUpperCase()] ?? 9) - (DISPOSAL_ORDER[(b.disposal_status || '').toUpperCase()] ?? 9)),
    [tools]
  );

  const sortedBols = useMemo(() => {
    const active = ['OPEN', 'LOADING', 'LOADED', 'REOPENED'];
    return [...bols].sort((a, b) => {
      const aA = active.includes((a.status || '').toUpperCase()) ? 0 : 1;
      const bA = active.includes((b.status || '').toUpperCase()) ? 0 : 1;
      return aA - bA;
    });
  }, [bols]);

  if (loading) return <div className="spd-page"><div className="spd-loading"><div className="spd-spinner" /></div></div>;
  if (error) return <div className="spd-page"><div className="spd-loading" style={{ color: 'var(--color-danger)' }}>{error}</div></div>;
  if (!project) return <div className="spd-page"><div className="spd-loading">Project not found</div></div>;

  return (
    <div className="spd-page">
      {/* Header */}
      <div className="spd-header">
        <button className="spd-back" onClick={() => navigate('/admin/disposal')}>&larr; Disposal Projects</button>
        <div className="spd-header-row">
          <h1 className="spd-project-num">{project.project_number}</h1>
          <ScrapProjectStatusBadge status={project.status} />
          {project.oem && <span className="spd-badge spd-badge-neutral">{project.oem}</span>}
          {project.site_name && <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{project.site_name}</span>}
        </div>
      </div>

      <div className="spd-content">
        {/* ============ SECTION 1: Project Details ============ */}
        <div className="spd-card">
          <div className="spd-card-header" onClick={() => toggleSection('details', !detailsCollapsed, setDetailsCollapsed)}>
            <span className="spd-card-title">Project Details</span>
            <div className="spd-card-actions" onClick={e => e.stopPropagation()}>
              {!editing && isActive && <button className="spd-btn spd-btn-secondary" onClick={startEdit}>Edit</button>}
            </div>
            <Chevron open={!detailsCollapsed} />
          </div>
          {!detailsCollapsed && (
            <div className="spd-card-body">
              {editing ? (
                <>
                  <div className="spd-grid">
                    {[
                      ['project_number', 'Project Number'], ['oem', 'OEM'],
                      ['supplier_name', 'Supplier Company'], ['site_name', 'Site Name'],
                      ['site_city', 'City'], ['site_state', 'State'],
                      ['hauler_name', 'Hauler'], ['estimated_weight_lbs', 'Est. Weight (lbs)'],
                      ['price_per_ton', 'Price/Ton ($)'],
                    ].map(([k, label]) => (
                      <div key={k} className="spd-field">
                        <span className="spd-field-label">{label}</span>
                        <input className="spd-input" value={editForm[k] ?? ''}
                          type={['estimated_weight_lbs', 'price_per_ton'].includes(k) ? 'number' : 'text'}
                          step={k === 'price_per_ton' ? '0.01' : undefined}
                          onChange={e => setEditForm(p => ({ ...p, [k]: e.target.value }))} />
                      </div>
                    ))}
                    <div className="spd-field spd-grid-full">
                      <span className="spd-field-label">Notes</span>
                      <textarea className="spd-input spd-textarea" value={editForm.notes || ''}
                        onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} />
                    </div>
                  </div>
                  {saveError && <div className="spd-error">{saveError}</div>}
                  <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-5)' }}>
                    <button className="spd-btn spd-btn-primary" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button className="spd-btn spd-btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="spd-grid">
                    {[
                      ['project_number', 'Project Number'], ['oem', 'OEM'],
                      ['supplier_name', 'Supplier Company'], ['site_name', 'Site Name'],
                      ['site_city', 'City'], ['site_state', 'State'],
                      ['hauler_name', 'Hauler'],
                      ['estimated_weight_lbs', 'Est. Weight (lbs)'],
                      ['price_per_ton', 'Price/Ton ($)'],
                    ].map(([k, label]) => (
                      <div key={k} className="spd-field">
                        <span className="spd-field-label">{label}</span>
                        <span className={project[k] ? 'spd-field-value' : 'spd-field-empty'}>
                          {project[k] != null && project[k] !== '' ? String(project[k]) : '—'}
                        </span>
                      </div>
                    ))}
                    {project.notes && (
                      <div className="spd-field spd-grid-full">
                        <span className="spd-field-label">Notes</span>
                        <span className="spd-field-value">{project.notes}</span>
                      </div>
                    )}
                  </div>
                  <div className="spd-audit">
                    <span>Created: {fmtDatetime(project.created_at)} by {project.created_by || '—'}</span>
                    <span>Updated: {fmtDatetime(project.updated_at)} by {project.updated_by || '—'}</span>
                  </div>
                  {isActive && (
                    <div style={{ marginTop: 'var(--space-6)' }}>
                      <button className="spd-btn spd-btn-danger" onClick={handleClose}>Close Project</button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* ============ SECTION 2: Assigned Tools ============ */}
        <div className="spd-card">
          <div className="spd-card-header" onClick={() => toggleSection('tools', !toolsCollapsed, setToolsCollapsed)}>
            <span className="spd-card-title">Assigned Tools</span>
            <div className="spd-card-actions" onClick={e => e.stopPropagation()}>
              {isActive && <button className="spd-btn spd-btn-primary" onClick={() => setShowToolPicker(true)}>+ Add Tools</button>}
            </div>
            <Chevron open={!toolsCollapsed} />
          </div>
          {!toolsCollapsed && (
            <div className="spd-card-body">
              <div className="spd-summary-line">
                {toolCounts.total} tools total
                {toolCounts.SHIPPED > 0 && ` — ${toolCounts.SHIPPED} shipped`}
                {toolCounts.STAGED > 0 && `, ${toolCounts.STAGED} staged`}
                {toolCounts.ON_SHIPMENT > 0 && `, ${toolCounts.ON_SHIPMENT} on shipment`}
                {toolCounts.ASSIGNED > 0 && `, ${toolCounts.ASSIGNED} assigned`}
                {toolCounts.WRITTEN_OFF > 0 && `, ${toolCounts.WRITTEN_OFF} written off`}
              </div>
              {sortedTools.length === 0 ? (
                <div className="spd-empty">No tools assigned to this project yet.</div>
              ) : (
                <table className="spd-table">
                  <thead><tr>
                    <th>Tool ID</th><th>Tool Type</th><th>Status</th>
                    <th>BOL</th><th>Est. Weight</th>{isActive && <th></th>}
                  </tr></thead>
                  <tbody>
                    {sortedTools.map(t => {
                      const tid = t.id || t.tool_id;
                      const ds = (t.disposal_status || '').toUpperCase();
                      const canWriteOff = isActive && (ds === 'ASSIGNED' || ds === 'STAGED');
                      return (
                        <tr key={tid}>
                          <td>{t.supplier_tool_id || '—'}</td>
                          <td>{t.tool_type || '—'}</td>
                          <td><DisposalStatusBadge status={t.disposal_status} /></td>
                          <td>{t.bol_id ? (bolLookup[t.bol_id] || t.bol_id.substring(0, 8) + '…') : '—'}</td>
                          <td>{t.estimated_weight != null ? `${Number(t.estimated_weight).toLocaleString()} lbs` : '—'}</td>
                          {isActive && (
                            <td className="spd-action-cell">
                              {canWriteOff && (
                                <button className="spd-btn-writeoff" title="Write Off" onClick={() => handleWriteOff(tid)}>Write Off</button>
                              )}
                              {canRemoveTools && canWriteOff && (
                                <button className="spd-btn-remove" title="Remove from Project"
                                  onClick={() => { setRemoveTarget(tid); setRemoveReason(''); setRemoveError(null); }}>
                                  Remove
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* ── Remove from Project confirm dialog ─────────────────────────── */}
        {removeTarget && (
          <div className="spd-overlay" onClick={() => { if (!removing) { setRemoveTarget(null); setRemoveError(null); } }}>
            <div className="spd-dialog" onClick={e => e.stopPropagation()}>
              <h3 className="spd-dialog-title">Remove from Project?</h3>
              <p className="spd-dialog-body">
                This tool will be returned to the eligible pool and can be assigned to another project.
              </p>
              <div className="spd-dialog-field">
                <label className="spd-dialog-label">Reason (optional)</label>
                <input type="text" className="spd-dialog-input" value={removeReason}
                  onChange={e => setRemoveReason(e.target.value)}
                  placeholder="Why is this tool being removed?" />
              </div>
              {removeError && <div className="spd-dialog-error">{removeError}</div>}
              <div className="spd-dialog-actions">
                <button className="spd-btn-secondary" disabled={removing}
                  onClick={() => { setRemoveTarget(null); setRemoveError(null); }}>Cancel</button>
                <button className="spd-btn-danger" disabled={removing}
                  onClick={handleRemoveConfirm}>
                  {removing ? 'Removing…' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============ SECTION 3: Bills of Lading ============ */}
        <div className="spd-card">
          <div className="spd-card-header" onClick={() => toggleSection('bols', !bolsCollapsed, setBolsCollapsed)}>
            <span className="spd-card-title">Bills of Lading</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <button className="spd-btn spd-btn-secondary" style={{ fontSize: 'var(--text-2xs)' }}
                onClick={e => { e.stopPropagation(); setShowManualBolModal(true); }}>
                + Create BOL
              </button>
              <Chevron open={!bolsCollapsed} />
            </div>
          </div>
          {!bolsCollapsed && (
            <div className="spd-card-body">
              {sortedBols.length === 0 ? (
                <div className="spd-empty">No bills of lading yet. BOLs are created by supplier operators via the mobile app.</div>
              ) : (
                <table className="spd-table">
                  <thead><tr>
                    <th>BOL Number</th><th>Status</th><th>Truck Type</th>
                    <th>Tools</th><th>Est. Weight</th><th>Actual Weight</th>
                    <th>Invoiced</th><th>Closed</th><th></th>
                  </tr></thead>
                  <tbody>
                    {sortedBols.map(b => {
                      const bs = (b.status || '').toUpperCase();
                      const isClosed = bs === 'CLOSED' || bs === 'AMENDED';
                      const uninvoicedClosed = isClosed && !b.invoiced;
                      return (
                        <tr key={b.bol_id}
                          className={`spd-bol-row ${uninvoicedClosed ? 'spd-bol-row-warn' : ''}`}
                          onClick={() => navigate(`/admin/disposal/${projectId}/bol/${b.bol_id}`, {
                            state: { project_id: projectId, site_id: siteId }
                          })}
                        >
                          <td style={{ fontWeight: 'var(--font-semibold)' }}>{b.bol_number || b.bol_id?.substring(0, 8)}</td>
                          <td><BolStatusBadge status={b.status} /></td>
                          <td>{b.truck_type || '—'}</td>
                          <td>{b.tool_count ?? (b.tools || []).length}</td>
                          <td>{b.estimated_weight_lbs != null ? `${Number(b.estimated_weight_lbs).toLocaleString()} lbs` : '—'}</td>
                          <td>
                            {b.weight_ticket_actual_lbs != null
                              ? `${Number(b.weight_ticket_actual_lbs).toLocaleString()} lbs`
                              : isClosed ? <span className="spd-warn-icon">—</span> : <span className="spd-dash">—</span>}
                          </td>
                          <td>
                            {b.invoiced ? <span className="spd-check-icon">✓</span>
                              : isClosed ? <span className="spd-warn-icon">—</span>
                              : <span className="spd-dash">—</span>}
                          </td>
                          <td>{fmtDate(b.closed_at)}</td>
                          <td onClick={e => e.stopPropagation()}>
                            {isClosed && (
                              <button className="spd-btn spd-btn-secondary" style={{ fontSize: 'var(--text-2xs)' }}
                                onClick={() => handleReopenBol(b.bol_id)}>Reopen</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tool picker modal */}
      {showToolPicker && (
        <ToolPickerModal
          siteId={siteId}
          onConfirm={handleAddTools}
          onCancel={() => setShowToolPicker(false)}
        />
      )}

      {/* Manual BOL modal */}
      {showManualBolModal && (
        <ManualBolModal
          projectId={projectId}
          siteId={siteId}
          projectNumber={project?.project_number || ''}
          existingBols={bols}
          tools={tools}
          callerEmail={callerEmail}
          onClose={() => setShowManualBolModal(false)}
          onCreated={() => { setShowManualBolModal(false); loadAll(); }}
        />
      )}

      {/* Toast */}
      {toast && <div className="spd-toast">{toast}</div>}
    </div>
  );
}
